import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Loader2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useDash, courseProgress } from "@/components/dashboard/Layout";
import { Card } from "@/components/dashboard/kit";
import { api, formatApiError } from "@/lib/api";

export default function CoursePlayer() {
  const { slug } = useParams();
  const { courses, progress, setProgress, approved } = useDash();
  const [busy, setBusy] = useState(null);
  const [active, setActive] = useState(0);
  const c = courses.find((x) => x.slug === slug);
  if (!c || !approved) return <Navigate to="/dashboard/courses" replace />;
  const done = progress[slug] || [];
  const p = courseProgress(progress, c);

  const toggle = async (i) => {
    setBusy(i);
    try { const r = await api.post("/me/progress", { course: slug, lesson: i, done: !done.includes(i) }); setProgress(r.data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  return (
    <div data-testid="course-player-page">
      <Link to="/dashboard/courses" className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--pink)]"><ArrowLeft size={16} /> My Courses</Link>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-4">{c.title}</h1>
      <p className="text-sm text-[var(--muted)] mt-1">{p.done}/{p.total} lessons complete · {p.pct}%</p>
      <div className="grid xl:grid-cols-[1.5fr_1fr] gap-6 mt-6">
        <Card className="!p-0 overflow-hidden">
          <div className="relative aspect-video">
            <img src={c.image} alt="" className="w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 grid place-items-center text-center p-6">
              <div><PlayCircle size={54} className="mx-auto text-[var(--pink)]" /><p className="font-display text-xl font-semibold mt-3">Lesson {active + 1}: {c.modules[active]}</p><p className="text-xs text-[var(--muted)] mt-1">Video for this lesson will be added by your mentor.</p></div>
            </div>
          </div>
          <div className="p-5 flex justify-end">
            <button data-testid="mark-active-lesson-btn" onClick={() => toggle(active)} className={done.includes(active) ? "btn-ghost text-sm" : "btn-neon text-sm"}>
              {busy === active ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} {done.includes(active) ? "Completed" : "Mark as complete"}
            </button>
          </div>
        </Card>
        <Card>
          <h2 className="font-display text-lg font-semibold mb-4">Lessons</h2>
          <ol className="space-y-2">
            {c.modules.map((m, i) => (
              <li key={m} className={`flex items-center gap-3 rounded-2xl px-3 py-3 text-sm border transition-colors ${active === i ? "border-[var(--pink)]" : "border-[var(--surface-border)]"}`}>
                <button data-testid={`lesson-toggle-${i}`} onClick={() => toggle(i)} aria-label="Toggle complete" className="text-[var(--pink)] shrink-0">
                  {busy === i ? <Loader2 className="animate-spin" size={18} /> : done.includes(i) ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                </button>
                <button data-testid={`lesson-open-${i}`} onClick={() => setActive(i)} className="text-left flex-1">{i + 1}. {m}</button>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </div>
  );
}
